import React, { useState, useRef, useEffect } from 'react';
import useScrollReveal from '../../hooks/useScrollReveal';
import SectionHeader from '../ui/SectionHeader';

interface FAQEntry {
  q: string;
  a: string;
}

const faqs: FAQEntry[] = [
  {
    q: 'Which regulatory frameworks does Actula support?',
    a: 'Actula covers CSRD (including all ESRS standards E1–E5, S1–S4, G1), EU Taxonomy (all six environmental objectives), CBAM, ISO 14067 for Product Carbon Footprint, the GHG Protocol Corporate Standard for Corporate Carbon Footprint, and BRSR for Indian listed companies.',
  },
  {
    q: 'How does Actula handle multi-entity and multi-plant reporting?',
    a: 'You define your reporting boundary and assign collection responsibilities to each entity. Each subsidiary sees only the disclosures relevant to its operations. Data is consolidated at group level automatically. Boundary decisions — operational control, financial control, or equity share — are documented and applied consistently across all modules.',
  },
  {
    q: 'Does Actula integrate with SAP Ariba?',
    a: 'Yes. The Sustainable Procurement module connects with SAP Ariba to bring sustainability data into the sourcing and purchasing workflow. Supplier sustainability scores are visible in Ariba sourcing events, approved supplier lists reflect ESG qualification status, and the procurement and sustainability processes are unified.',
  },
  {
    q: 'How does Actula handle the EU Taxonomy alongside CSRD?',
    a: 'Actula implements EU Taxonomy and CSRD as one integrated collection pathway, not two separate exercises. Double materiality and IRO identification inform both ESRS disclosure scope and Taxonomy eligibility assessment. Data collected for ESRS standards is reused for Taxonomy KPI calculation where it overlaps — energy data serving both E1 and Taxonomy climate alignment, for example.',
  },
  {
    q: 'How quickly can we be up and running?',
    a: 'Most clients are collecting structured data within two weeks of onboarding. We work with your procurement, sustainability, and finance teams to configure Actula for your specific regulatory obligations, entity structure, and reporting timeline.',
  },
  {
    q: 'Is Actula suitable for companies outside the EU subject to CSRD?',
    a: 'Yes. CSRD applies to large non-EU companies with significant EU turnover or listed EU subsidiaries. Actula is built for enterprises operating across both India (BRSR) and the EU (CSRD, CBAM, EU Taxonomy) within one platform — it does not require EU headquarters to use it.',
  },
];

interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}

function FAQItem({ question, answer, isOpen, onToggle }: FAQItemProps): React.ReactElement {
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    el.style.maxHeight = isOpen ? `${el.scrollHeight}px` : '0';
    el.style.opacity = isOpen ? '1' : '0';
  }, [isOpen]);

  return (
    <div className={`faq-item${isOpen ? ' faq-item--open' : ''}`}>
      <button
        className="faq-trigger"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        {question}
        <span className="faq-chevron">
          <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <polyline points="2,5 7,10 12,5" />
          </svg>
        </span>
      </button>
      <div
        className="faq-body"
        ref={bodyRef}
        style={{ maxHeight: 0, opacity: 0, overflow: 'hidden', transition: 'max-height 0.35s ease, opacity 0.25s ease' }}
      >
        <div className="faq-body-inner">{answer}</div>
      </div>
    </div>
  );
}

export default function FAQ(): React.ReactElement {
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const ref = useScrollReveal();

  const toggle = (i: number): void => setOpenIdx(prev => (prev === i ? null : i));

  return (
    <section
      className="faq section-pad"
      id="faq"
      ref={ref as React.RefObject<HTMLElement>}
    >
      <div className="container">
        <SectionHeader
          eyebrow="Questions"
          title="Frequently asked questions"
          description="Common questions about Actula, its modules, and how it handles your specific regulatory obligations."
        />
        <div className="faq-list reveal">
          {faqs.map((item, i) => (
            <FAQItem
              key={i}
              question={item.q}
              answer={item.a}
              isOpen={openIdx === i}
              onToggle={() => toggle(i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
